import React, { useCallback, useEffect, useState } from "react";
import {
    TimesheetEditApplyResult,
    TimesheetEditBlockReasons,
    TimesheetEditMappingItem,
    TimesheetEditPrepareResult,
    TimesheetEditTargetLine,
    TimesheetEditValidateResult
} from "../../../../../store/TimesheetApprovalStore";
import { ConfirmationDialog } from "../../../../common/ConfirmationDialog";
import { FormatDate } from "../../../../utils/common";
import { getTimesheetLineName } from "../timesheetApprovalFields";
import ApplyResultDialog from "./ApplyResultDialog";
import { buildApplyResultMessages } from "./common";
import EditApprovalRequestPanel from "./EditApprovalRequestPanel";

interface Props {
    requestId: string;
    editPrepare: (requestId: string, callback?: (result: TimesheetEditPrepareResult) => void) => void;
    editValidate: (
        requestId: string,
        targetLine: TimesheetEditTargetLine,
        mapping: TimesheetEditMappingItem[],
        callback?: (result: TimesheetEditValidateResult) => void
    ) => void;
    editApply: (
        requestId: string,
        targetLine: TimesheetEditTargetLine,
        mapping: TimesheetEditMappingItem[],
        callback?: (result: TimesheetEditApplyResult) => void,
        onError?: () => void
    ) => void;
    onClose: () => void;
}

const EditApprovalRequest = (props: Props) => {
    const { requestId, editPrepare, editValidate, editApply, onClose } = props;
    const [result, setResult] = useState<TimesheetEditPrepareResult | null>(null);
    const [applyMessages, setApplyMessages] = useState<string[] | null>(null);

    useEffect(() => {
        editPrepare(requestId, setResult);
    }, [requestId]);

    const onApplied = useCallback(
        (applyResult: TimesheetEditApplyResult) => {
            const messages = buildApplyResultMessages(applyResult);
            if (messages.length > 0) {
                setApplyMessages(messages);
            } else {
                onClose();
            }
        },
        [onClose]
    );

    if (applyMessages) {
        return <ApplyResultDialog messages={applyMessages} onDismiss={onClose} />;
    }

    if (!result) {
        return null;
    }

    const sourceName = getTimesheetLineName(result.sourceLine.isAdministrative, result.sourceProject);

    switch (result.blockReason) {
        case TimesheetEditBlockReasons.None:
            return (
                <EditApprovalRequestPanel
                    requestId={requestId}
                    prepared={result}
                    editValidate={editValidate}
                    editApply={editApply}
                    onApplied={onApplied}
                    onClose={onClose}
                />
            );
        case TimesheetEditBlockReasons.ClosedPeriods:
            return (
                <ConfirmationDialog
                    onDismiss={onClose}
                    onYes={onClose}
                    yesButtonProps={{ text: "OK" }}
                    dialogContentProps={{ title: "Request belongs to a closed period" }}>
                    <p>
                        Approval requests can be edited only when they belong to an open time reporting period. The period{" "}
                        {FormatDate(result.period.startDate)} - {FormatDate(result.period.endDate)} of this request is
                        closed.
                    </p>
                </ConfirmationDialog>
            );
        case TimesheetEditBlockReasons.SourceProjectTimeTrackingDisabled:
            return (
                <ConfirmationDialog
                    onDismiss={onClose}
                    onYes={onClose}
                    yesButtonProps={{ text: "OK" }}
                    dialogContentProps={{ title: "Time tracking is disabled for the source project" }}>
                    <p>
                        Time cannot be moved from "{sourceName}" because the Allow time to be tracked for the project setting
                        is turned off for it. Please turn the setting on for the project, then try again.
                    </p>
                </ConfirmationDialog>
            );
        case TimesheetEditBlockReasons.NoResourcePermission:
            return (
                <ConfirmationDialog
                    onDismiss={onClose}
                    onYes={onClose}
                    yesButtonProps={{ text: "OK" }}
                    dialogContentProps={{ title: "No permission to edit the timesheet" }}>
                    <p>
                        You do not have permission to edit the time of {result.timesheetOwner?.fullName ?? "this resource"}.
                        Please contact your administrator if you need to edit this approval request.
                    </p>
                </ConfirmationDialog>
            );
        default:
            return null;
    }
};

export default EditApprovalRequest;
